import { useEffect, useState } from "react"
import { Alert, Box, Skeleton, Typography } from "@mui/material"
import { log } from "helpers/logger"
import type { SxProps, Theme } from "@mui/material"
import type { SkillPackageArtifact } from "./skill-package-artifact"
import { SkillPackageFileList } from "./skill-package-file-list"
import { SkillPackageFileView } from "./skill-package-file-view"
import { EXPLORER_CONTAINER_NAME, EXPLORER_RAIL_WIDTH_PX, WIDE_EXPLORER_QUERY } from "./skill-package-layout"
import { type SkillPackageContents, isSkillManifestPath, readSkillPackage } from "./skill-package-contents"

const ROOT_SX = {
  flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5,
  containerType: "inline-size", containerName: EXPLORER_CONTAINER_NAME,
} satisfies SxProps<Theme>
const DESCRIPTION_SX = { color: "text.secondary", pb: 1.5, borderBottom: "1px solid var(--app-bg-muted)" } satisfies SxProps<Theme>
const EXPLORER_SX = {
  flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5,
  [WIDE_EXPLORER_QUERY]: { flexDirection: "row", gap: 2, alignItems: "stretch" },
} satisfies SxProps<Theme>
const LIST_PANE_SX = {
  minWidth: 0, flexShrink: 0, pb: 1.5, borderBottom: 1, borderColor: "divider",
  [WIDE_EXPLORER_QUERY]: { width: `${EXPLORER_RAIL_WIDTH_PX}px`, pb: 0, pr: 2, borderBottom: 0, borderRight: 1, borderColor: "divider", overflow: "auto" },
} satisfies SxProps<Theme>
const CONTENT_PANE_SX = { flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", [WIDE_EXPLORER_QUERY]: { overflow: "auto" } } satisfies SxProps<Theme>
const LOADING_SX = { minHeight: "8rem" } satisfies SxProps<Theme>

type LoadState =
  | { bundle: string, status: "loading" }
  | { bundle: string, status: "loaded", contents: SkillPackageContents }
  | { bundle: string, status: "error" }

function PackageExplorer({ contents }: { contents: SkillPackageContents }) {
  const { entries, omittedEntryCount } = contents
  const [requestedPath, setRequestedPath] = useState<string | null>(null)

  const selected = entries.find((entry) => entry.path === requestedPath)
    ?? entries.find((entry) => isSkillManifestPath(entry.path))
    ?? entries[0]
    ?? null

  if (selected === null) {
    return <Alert severity="warning" data-testid="kelly-skill-package-empty">This package contains no files.</Alert>
  }

  const hasManifest = entries.some((entry) => isSkillManifestPath(entry.path))

  return (
    <>
      {!hasManifest && (
        <Alert severity="warning" data-testid="kelly-skill-package-no-manifest">
          This package has no SKILL.md.
        </Alert>
      )}
      <Box sx={EXPLORER_SX}>
        <Box sx={LIST_PANE_SX} data-testid="kelly-skill-package-rail">
          <SkillPackageFileList
            entries={entries}
            selectedPath={selected.path}
            onSelect={setRequestedPath}
            omittedEntryCount={omittedEntryCount}
          />
          {omittedEntryCount > 0 && (
            <Typography variant="caption" color="text.secondary" data-testid="kelly-skill-package-omitted">
              {`${omittedEntryCount} more file${omittedEntryCount === 1 ? "" : "s"} not listed.`}
            </Typography>
          )}
        </Box>
        <Box sx={CONTENT_PANE_SX} data-testid="kelly-skill-package-content">
          <SkillPackageFileView entry={selected} />
        </Box>
      </Box>
    </>
  )
}

export function SkillPackagePanelBody({ artifact }: { artifact: SkillPackageArtifact }) {
  const { bundle, description } = artifact
  const [state, setState] = useState<LoadState>(() => ({ bundle, status: "loading" }))

  useEffect(() => {
    let cancelled = false
    readSkillPackage(bundle).then(
      (contents) => { if (!cancelled) setState({ bundle, status: "loaded", contents }) },
      (error: unknown) => {
        log(error)
        if (!cancelled) setState({ bundle, status: "error" })
      },
    )
    return () => { cancelled = true }
  }, [bundle])

  const current = state.bundle === bundle ? state : { bundle, status: "loading" as const }

  return (
    <Box sx={ROOT_SX} data-testid="kelly-skill-package-panel">
      {description !== "" && <Typography variant="body2" sx={DESCRIPTION_SX} data-testid="kelly-skill-package-description">{description}</Typography>}
      {current.status === "loading" && <Skeleton variant="rectangular" sx={LOADING_SX} data-testid="kelly-skill-package-loading" />}
      {current.status === "error" && (
        <Alert severity="warning" data-testid="kelly-skill-package-error">
          This package couldn&rsquo;t be opened for preview.
        </Alert>
      )}
      {current.status === "loaded" && <PackageExplorer key={bundle} contents={current.contents} />}
    </Box>
  )
}
